import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type {
  AdmissionApplication, AdmissionCycle, SeatSnapshot,
} from '../../../types/admissions';
import {
  Page, Section, DataTable, Pill, StatStrip, applicationStatusTone,
  type DataTableColumn,
  pdfColors, pdfFontSize, pdfLetter, pdfSpacing,
  fmtClassName, fmtPercent, fmtNumber, fmtMarks, humanize,
  type SchoolBranding, type DocumentMeta,
} from '../../../utils/pdf';
import { assessmentPlan, pathwayFor, roundsLabel } from '../../../utils/admissionAssessment';

interface Props {
  apps: AdmissionApplication[];
  cycle: AdmissionCycle;
  className?: string;
  seatSnapshot?: SeatSnapshot | null;
  branding: SchoolBranding;
  meta: DocumentMeta;
}

const styles = StyleSheet.create({
  rank: { fontSize: pdfFontSize.md, fontWeight: 700, textAlign: 'center' },
  rankInside: { color: pdfColors.success },
  name: { fontSize: pdfFontSize.sm, fontWeight: 600, color: pdfColors.textPrimary },
  score: { fontSize: pdfFontSize.md, fontWeight: 700, textAlign: 'right' },

  // Full-width marker between the last seat inside the cap and the first
  // outside it.
  cut: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
  },
  cutLine: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: pdfColors.successBorder,
    borderTopStyle: 'dashed',
  },
  cutText: {
    marginHorizontal: pdfSpacing.sm,
    fontSize: pdfFontSize.xxs,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel,
    color: pdfColors.success,
  },
});

interface MeritRow {
  app: AdmissionApplication;
  /** Null until every mark is in — shown as "—", never a made-up place. */
  rank: number | null;
  testPct: number | null;
  interviewAbs: number | null;
  insideCap: boolean;
}

function pctFromTestScores(app: AdmissionApplication): number | null {
  const papers = app.testScores || [];
  if (papers.length === 0) return null;
  const totalMax = papers.reduce((s, p) => s + (p.maxMarks || 0), 0);
  const totalObt = papers.reduce((s, p) => s + (p.absent ? 0 : (p.obtained || 0)), 0);
  if (totalMax <= 0) return null;
  return (totalObt / totalMax) * 100;
}

function interviewTotal(app: AdmissionApplication): number | null {
  const rounds = app.interviewRounds || [];
  if (rounds.length === 0) return null;
  return rounds.reduce((s, r) => s + (r.totalMarks || 0), 0);
}

/** MeritListDocument — landscape ranked list for one class. A dashed
 *  cut-line row marks where the seat cap falls, so the board sees at a
 *  glance who is inside and who is waiting. */
const MeritListDocument: React.FC<Props> = ({
  apps, cycle, className, seatSnapshot, branding, meta,
}) => {
  const rows: MeritRow[] = apps.map((app) => {
    const rank = app.meritRank ?? null;
    return {
      app,
      rank,
      testPct: pctFromTestScores(app),
      interviewAbs: interviewTotal(app),
      insideCap: !!seatSnapshot && rank != null && rank <= seatSnapshot.hardCap,
    };
  });
  const ranked = rows.filter((r) => r.rank != null).length;
  // The cut-line sits after the last ranked applicant inside the cap.
  const firstOutside = seatSnapshot ? rows.findIndex((r) => r.rank != null && !r.insideCap) : -1;
  // Columns follow the class's pathway: no Test column for a class that
  // sits none, and the rounds named as they are ("Observation & interview").
  const plan = assessmentPlan(pathwayFor(cycle, apps[0]?.classId));
  const roundsMax = (plan.interview?.rounds || []).reduce((t, r) => t + r.maxMarks, 0);
  const roundsHeader = roundsLabel(plan.interview?.rounds || []);
  const label = fmtClassName(className);

  const columns: Array<DataTableColumn<MeritRow>> = [
    { key: 'rank', header: 'Rank', width: 46, align: 'center',
      render: (r) => <Text style={r.insideCap ? [styles.rank, styles.rankInside] : styles.rank}>{r.rank ?? '—'}</Text> },
    { key: 'name', header: 'Applicant', flex: 2.4,
      render: (r) => <Text style={styles.name}>{r.app.name || '—'}</Text> },
    { key: 'father', header: "Father's name", flex: 2, render: (r) => r.app.fatherName || '—' },
    ...(plan.test ? [{ key: 'test', header: 'Test', width: 60, align: 'right' as const, render: (r: MeritRow) => fmtPercent(r.testPct, 1) }] : []),
    ...(plan.interview ? [{
      key: 'intv', header: roundsHeader, width: 110, align: 'right' as const,
      render: (r: MeritRow) => (r.interviewAbs == null ? '—' : `${fmtMarks(r.interviewAbs)} / ${fmtMarks(roundsMax)}`),
    }] : []),
    { key: 'prev', header: 'Prev. result', width: 72, align: 'right',
      render: (r) => fmtPercent(r.app.previousPercentage ?? null, 0) },
    { key: 'merit', header: 'Merit score', width: 76, align: 'right',
      render: (r) => <Text style={styles.score}>{fmtNumber(r.app.computedMeritScore ?? null, 2)}</Text> },
    { key: 'status', header: 'Status', width: 128,
      render: (r) => <Pill dense label={humanize(r.app.status)} tone={applicationStatusTone(r.app.status)} /> },
  ];

  const metaSubtitled: DocumentMeta = {
    ...meta,
    subtitle: meta.subtitle || [cycle.name, label].filter(Boolean).join('  ·  '),
  };

  return (
    <Document title={meta.title} author={branding.name}>
      <Page
        branding={branding}
        meta={metaSubtitled}
        orientation="landscape"
        official={{ details: [{ value: cycle.name }, ...(label ? [{ value: label }] : []), { value: `${rows.length} applicant${rows.length === 1 ? '' : 's'}` }] }}
      >
        {seatSnapshot && (
          <StatStrip
            items={[
              { label: 'Seats', value: seatSnapshot.totalSeats },
              { label: 'Seat cap', value: seatSnapshot.hardCap, caption: `Includes ${seatSnapshot.overbookPercent}% overbooking` },
              { label: 'Offers out', value: seatSnapshot.live, tone: 'info' },
              { label: 'Seats left', value: seatSnapshot.remaining, tone: 'success' },
              { label: 'Ranked', value: rows.length },
            ]}
          />
        )}

        <Section title={`Merit list${label ? ` · ${label}` : ''}`} caption={ranked === rows.length ? `${ranked} applicant${ranked === 1 ? '' : 's'} ranked` : `${ranked} of ${rows.length} applicants ranked`}>
          <DataTable
            columns={columns}
            rows={rows}
            compact
            zebra
            rowDivider={(_r, i) => (i === firstOutside && i > 0 ? (
              <View style={styles.cut} wrap={false}>
                <View style={styles.cutLine} />
                <Text style={styles.cutText}>Seat cut-line · {seatSnapshot?.hardCap} seats</Text>
                <View style={styles.cutLine} />
              </View>
            ) : null)}
          />
        </Section>
      </Page>
    </Document>
  );
};

export default MeritListDocument;
